import { defaultLocale } from "@/i18n/config";

/** Inclusive calendar-day convention for exhibition and activity bounds. */
export const EVENT_CALENDAR_TIMEZONE = "Europe/Paris";

const CALENDAR_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function readPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): number {
  const value = parts.find((part) => part.type === type)?.value;
  if (!value) {
    throw new Error(`Missing DateTimeFormat part: ${type}`);
  }
  return Number(value);
}

function getZonedParts(date: Date, timeZone: string): ZonedParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date);
  return {
    year: readPart(parts, "year"),
    month: readPart(parts, "month"),
    day: readPart(parts, "day"),
    hour: readPart(parts, "hour"),
    minute: readPart(parts, "minute"),
    second: readPart(parts, "second"),
  };
}

function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const utc = getZonedParts(date, "UTC");
  const zoned = getZonedParts(date, timeZone);

  const utcMs = Date.UTC(
    utc.year,
    utc.month - 1,
    utc.day,
    utc.hour,
    utc.minute,
    utc.second,
  );
  const zonedMs = Date.UTC(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second,
  );

  return zonedMs - utcMs;
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, "0");
}

function parseCalendarDay(
  value: string,
): { year: number; month: number; day: number } | null {
  const trimmed = value.trim();
  if (!CALENDAR_DAY_RE.test(trimmed)) return null;

  const [yearRaw, monthRaw, dayRaw] = trimmed.split("-");
  if (!yearRaw || !monthRaw || !dayRaw) return null;

  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const utcProbe = new Date(Date.UTC(year, month - 1, day));
  if (
    utcProbe.getUTCFullYear() !== year ||
    utcProbe.getUTCMonth() + 1 !== month ||
    utcProbe.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
}

function zonedWallClockToUtc(
  calendarDay: string,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
  timeZone: string,
): Date {
  const parsed = parseCalendarDay(calendarDay);
  if (!parsed) {
    throw new Error(`Invalid calendar day: ${calendarDay}`);
  }

  const { year, month, day } = parsed;
  const wallClock = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}.${pad(millisecond, 3)}`;

  let utcMs = Date.parse(`${wallClock}Z`);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const offset = getTimeZoneOffsetMs(timeZone, new Date(utcMs));
    const next = Date.parse(`${wallClock}Z`) - offset;
    if (next === utcMs) break;
    utcMs = next;
  }

  return new Date(utcMs);
}

export function isEventCalendarDay(value: string): boolean {
  return parseCalendarDay(value) !== null;
}

/** timestamptz ISO → YYYY-MM-DD (Europe/Paris) for date pickers. */
export function eventIsoToCalendarDay(iso: string | null | undefined): string {
  if (!iso?.trim()) return "";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: EVENT_CALENDAR_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** timestamptz ISO → localized display date (Europe/Paris calendar day). */
export function formatEventCalendarDate(
  iso: string | null | undefined,
  locale: string = defaultLocale,
): string {
  if (!iso?.trim()) return "—";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(locale, {
    timeZone: EVENT_CALENDAR_TIMEZONE,
    dateStyle: "medium",
  }).format(date);
}

/** YYYY-MM-DD picker → start timestamptz (00:00:00.000 Europe/Paris). */
export function eventCalendarDayToStartIso(day: string): string | null {
  const parsed = parseCalendarDay(day);
  if (!parsed) return null;

  const calendarDay = `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`;
  return zonedWallClockToUtc(
    calendarDay,
    0,
    0,
    0,
    0,
    EVENT_CALENDAR_TIMEZONE,
  ).toISOString();
}

/** YYYY-MM-DD picker → end timestamptz (23:59:59.999 Europe/Paris). */
export function eventCalendarDayToEndIso(day: string): string | null {
  const parsed = parseCalendarDay(day);
  if (!parsed) return null;

  const calendarDay = `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`;
  return zonedWallClockToUtc(
    calendarDay,
    23,
    59,
    59,
    999,
    EVENT_CALENDAR_TIMEZONE,
  ).toISOString();
}

/** Add (or subtract) whole calendar days to a YYYY-MM-DD value. */
export function addEventCalendarDays(day: string, days: number): string | null {
  const parsed = parseCalendarDay(day);
  if (!parsed) return null;

  const utc = Date.UTC(parsed.year, parsed.month - 1, parsed.day + days);
  const next = new Date(utc);
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/**
 * Exclusive upper bound for inclusive calendar-day filters:
 * first instant of the following Europe/Paris day (`end_date < this`).
 * Avoids excluding timestamps stored past 23:59:59.999.
 */
export function eventCalendarDayToExclusiveEndIso(day: string): string | null {
  const nextDay = addEventCalendarDays(day, 1);
  if (!nextDay) return null;
  return eventCalendarDayToStartIso(nextDay);
}

/** Current calendar day in Europe/Paris (YYYY-MM-DD). */
export function parisTodayCalendarDay(reference: Date = new Date()): string {
  return eventIsoToCalendarDay(reference.toISOString());
}

export function areEventDatesInOrder(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): boolean {
  if (!startIso?.trim() || !endIso?.trim()) return false;

  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

  return end.getTime() >= start.getTime();
}
